import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FilePlus2, FileText, Send } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import { cashReceipts as cashReceiptsApi, orders as ordersApi } from '@/api/endpoints'
import { ConfirmButton, Spinner } from '@/components/ui'
import { currencySymbol, formatAmount } from '@/lib/utils'
import type { OrderDetail, OrderDocument, Payment } from '@/types/api'

import InvoiceDialog from './InvoiceDialog'

/** Poplatok platobnej brány (Stripe) a čo reálne príde na účet školy.
 *  Kým Stripe transakciu nezaúčtuje, poplatok ešte nie je známy - riadok
 *  sa neukáže a doplní sa pri ďalšom otvorení objednávky. */
export function ProviderFee({ payment }: { payment: Payment }) {
  const { t } = useTranslation()
  if (payment.fee == null || payment.net == null) return null
  const symbol = payment.fee_currency ? currencySymbol(payment.fee_currency.toUpperCase()) : currencySymbol()
  return (
    <span className="ml-2 text-xs text-slate-500" title={t('payments.providerFeeHint')}>
      {t('payments.providerFee', {
        fee: `${formatAmount(payment.fee)} ${symbol}`,
        net: `${formatAmount(payment.net)} ${symbol}`,
      })}
    </span>
  )
}

/** Číslo dokladu k platbe - odkaz na PDF (potvrdenie alebo VP). */
export function DocumentLink({ document }: { document: OrderDocument }) {
  const { t } = useTranslation()
  return (
    <a
      href={cashReceiptsApi.pdfUrl(document.id)}
      target="_blank"
      rel="noopener noreferrer"
      title={t('payments.documentOpen')}
      className="ml-2 inline-flex items-center gap-1 rounded border border-brand-200 px-1.5 py-0.5 text-xs text-brand-700 hover:bg-brand-50"
    >
      <FileText size={12} />
      {document.number}
    </a>
  )
}

/** Doklad k platbe podľa jej id. */
export function documentOf(order: OrderDetail | null | undefined, paymentId: number) {
  return order?.documents?.find((doc) => doc.payment_id === paymentId)
}

/**
 * „Odoslať doklady klientovi (N)“ - jeden e-mail s dokladmi objednávky,
 * ktoré ešte neodišli (`POST /orders/<vs>/send-documents`). Doklady sa
 * automaticky neposielajú; posiela ich manažér, keď chce.
 */
export function SendDocumentsButton({ order, readOnly }: { order: OrderDetail; readOnly?: boolean }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [message, setMessage] = useState<string | null>(null)
  // Faktúra ide klientovi spolu s potvrdeniami; stornovaná, ktorá ešte
  // neodišla, už nie. Storno odíde len k faktúre, ktorú klient dostal.
  const unsent =
    (order.documents ?? []).filter((doc) => !doc.sent_at).length +
    (order.invoices ?? []).filter((invoice) => !invoice.storno && !invoice.sent_at).length +
    (order.invoices ?? []).filter((invoice) => invoice.storno && invoice.sent_at && !invoice.storno_sent_at).length
  const hasEmail = Boolean(order.client?.email)

  const send = useMutation({
    mutationFn: () => ordersApi.sendDocuments(order.payment_ref),
    onSuccess: ({ sent }) => {
      setMessage(t('payments.sendDocumentsDone', { count: sent }))
      void qc.invalidateQueries({ queryKey: ['order'] })
    },
    onError: (err) => setMessage(apiErrorMessage(err)),
  })

  const sendable =
    (order.documents?.length ?? 0) +
    (order.invoices ?? []).filter((invoice) => !invoice.storno || invoice.sent_at).length
  if (!sendable || readOnly) return null
  return (
    <div className="flex flex-wrap items-center justify-end gap-2 text-xs">
      {message ? <span className="text-slate-600">{message}</span> : null}
      {unsent ? (
        <button
          type="button"
          className="btn-secondary px-2 py-1 text-xs"
          disabled={!hasEmail || send.isPending}
          title={hasEmail ? order.client?.email ?? '' : t('payments.sendDocumentsNoEmail')}
          onClick={() => send.mutate()}
        >
          {send.isPending ? <Spinner /> : <Send size={13} />}
          {t('payments.sendDocuments', { count: unsent })}
        </button>
      ) : message ? null : (
        <span className="text-slate-500">{t('payments.allDocumentsSent')}</span>
      )}
    </div>
  )
}

/**
 * Faktúra objednávky - odkaz na PDF a storno, alebo „Vystaviť faktúru“.
 * Jedna platná faktúra na objednávku; stornovaná ostáva v zozname
 * prečiarknutá a k objednávke sa dá vystaviť nová.
 */
export function InvoiceButtons({ order, readOnly }: { order: OrderDetail; readOnly?: boolean }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const invoices = order.invoices ?? []
  const active = invoices.find((invoice) => !invoice.storno)

  const cancel = useMutation({
    mutationFn: (id: number) => ordersApi.cancelInvoice(id),
    onSuccess: (invoice) => {
      setError(null)
      void qc.invalidateQueries({ queryKey: ['order'] })
      window.open(ordersApi.invoicePdfUrl(invoice.id, true), '_blank', 'noopener')
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  if (Number.parseFloat(order.total || '0') <= 0 && !invoices.length) return null
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      {error ? <span className="text-red-600">{error}</span> : null}
      {invoices.map((invoice) => (
        <span key={invoice.id} className="inline-flex items-center gap-1">
          {/* Pôvodná faktúra ostáva taká, ako bola vystavená; storno je
              samostatný doklad z radu OFA. */}
          <a
            href={ordersApi.invoicePdfUrl(invoice.id)}
            target="_blank"
            rel="noopener noreferrer"
            title={invoice.storno ? t('invoice.cancelled') : t('invoice.open')}
            className={
              invoice.storno
                ? 'inline-flex items-center gap-1 rounded border border-slate-200 px-1.5 py-0.5 text-slate-400 line-through hover:bg-slate-50'
                : 'inline-flex items-center gap-1 rounded border border-brand-200 px-1.5 py-0.5 text-brand-700 hover:bg-brand-50'
            }
          >
            <FileText size={12} />
            {invoice.number}
          </a>
          {invoice.storno && invoice.storno_number ? (
            <a
              href={ordersApi.invoicePdfUrl(invoice.id, true)}
              target="_blank"
              rel="noopener noreferrer"
              title={t('invoice.stornoOpen')}
              className="inline-flex items-center gap-1 rounded border border-red-200 px-1.5 py-0.5 text-red-700 hover:bg-red-50"
            >
              <FileText size={12} />
              {invoice.storno_number}
            </a>
          ) : null}
        </span>
      ))}
      {readOnly ? null : active ? (
        <ConfirmButton
          className="text-slate-500 hover:text-red-600 hover:underline"
          message={t('invoice.cancelConfirm', { number: active.number })}
          onConfirm={() => cancel.mutate(active.id)}
        >
          {t('invoice.cancel')}
        </ConfirmButton>
      ) : (
        <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={() => setOpen(true)}>
          <FilePlus2 size={13} />
          {t('invoice.create')}
        </button>
      )}
      {open ? <InvoiceDialog paymentRef={order.payment_ref} onClose={() => setOpen(false)} /> : null}
    </div>
  )
}
